import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit, fetchTrend, fetchRecommend, fetchBundle, fetchInsight, fetchMock } from '../services';

/** Initial state */
const initialState = {
    apiCallStatusTrack: {
        guide: false,
        tags: false,
        artefacts: false,
        artefact: false,
        artefactUnit: false
    },
    data: null,
    error: null,
    guide: [],
    tags: [],
    artefacts: [],
    artefact: {},
    artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[OPTIMIZER]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
    [
        'OPTIMIZER_DATA_REQUEST',
        'OPTIMIZER_DATA_SUCCESS',
        'OPTIMIZER_DATA_ERROR',
        'OPTIMIZER_DATA_RESET',
        'LOOKUP_OPTIMIZER_GUIDE',
        'SET_OPTIMIZER_GUIDE',
        'SET_OPTIMIZER_GUIDE_ON',
        'LOOKUP_OPTIMIZER_TAGS',
        'SET_OPTIMIZER_TAGS',
        'LOOKUP_OPTIMIZER_ARTEFACTS',
        'SET_OPTIMIZER_ARTEFACTS',
        'LOOKUP_OPTIMIZER_ARTEFACT',
        'SET_OPTIMIZER_ARTEFACT',
        'LOOKUP_OPTIMIZER_ARTEFACT_UNIT',
        'SET_OPTIMIZER_ARTEFACT_UNIT',
        'RESET_OPTIMIZER'
    ],
    namespacedPrefix
);

const {
    OPTIMIZER_DATA_REQUEST,
    OPTIMIZER_DATA_SUCCESS,
    OPTIMIZER_DATA_ERROR,
    OPTIMIZER_DATA_RESET,
    LOOKUP_OPTIMIZER_GUIDE,
    SET_OPTIMIZER_GUIDE,
    SET_OPTIMIZER_GUIDE_ON,
    LOOKUP_OPTIMIZER_TAGS,
    SET_OPTIMIZER_TAGS,
    LOOKUP_OPTIMIZER_ARTEFACTS,
    SET_OPTIMIZER_ARTEFACTS,
    LOOKUP_OPTIMIZER_ARTEFACT,
    SET_OPTIMIZER_ARTEFACT,
    LOOKUP_OPTIMIZER_ARTEFACT_UNIT,
    SET_OPTIMIZER_ARTEFACT_UNIT,
    RESET_OPTIMIZER
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
    /** auth data request */
    [OPTIMIZER_DATA_REQUEST](state) {
        Object.assign(state, { error: null });
    },

    /** auth data success */
    [OPTIMIZER_DATA_SUCCESS](state, payload) {
        Object.assign(state, { apiCallStatusTrack: initialState.apiCallStatusTrack, data: payload, user: payload, isAuthenticated: true });
    },

    /** auth data error */
    [OPTIMIZER_DATA_ERROR](state, payload) {
        Object.assign(state, {
            apiCallStatusTrack: initialState.apiCallStatusTrack,
            data: null,
            error: payload || true
        });
    },

    /** reset auth data */
    [OPTIMIZER_DATA_RESET](state) {
        Object.assign(state, initialState);
    },

    [LOOKUP_OPTIMIZER_GUIDE](state) {
        state.apiCallStatusTrack.guide = true;
    },
    [SET_OPTIMIZER_GUIDE](state, payload) {
        state.guide = payload.res.data;
        state.apiCallStatusTrack.guide = false;
    },
    [SET_OPTIMIZER_GUIDE_ON](state, guide) {
        state.guideOn = guide;
    },
    [LOOKUP_OPTIMIZER_TAGS](state) {
        state.apiCallStatusTrack.tags = true;
    },
    [SET_OPTIMIZER_TAGS](state, payload) {
        state.tags = payload.res.data;
        state.tagsCount = payload.res.data ? payload.res.data.length : 0;
        state.apiCallStatusTrack.tags = false;
    },
    [LOOKUP_OPTIMIZER_ARTEFACTS](state) {
        state.apiCallStatusTrack.artefacts = true;
    },
    [SET_OPTIMIZER_ARTEFACTS](state, payload) {
        state.artefacts = payload.artefacts;
        state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
        state.apiCallStatusTrack.artefacts = false;
    },
    [LOOKUP_OPTIMIZER_ARTEFACT](state) {
        state.apiCallStatusTrack.artefact = true;
    },
    [SET_OPTIMIZER_ARTEFACT](state, payload) {
        state.artefact = {};
        state.artefact = payload;
        state.apiCallStatusTrack.artefact = false;
    },
    [LOOKUP_OPTIMIZER_ARTEFACT_UNIT](state) {
        state.apiCallStatusTrack.artefactUnit = true;
    },
    [SET_OPTIMIZER_ARTEFACT_UNIT](state, payload) {
        state.artefactUnit = payload;
        state.apiCallStatusTrack.artefactUnit = false;
    },
    [RESET_OPTIMIZER](state) {
        for (let f in state) {
            Vue.set(state, f, state.initialState[f]);
        }
    }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
    'FETCH_OPTIMIZER_GUIDE',
    'FETCH_OPTIMIZER_TAGS',
    'FETCH_OPTIMIZER_ARTEFACTS',
    'FETCH_OPTIMIZER_ARTEFACT',
    'FETCH_OPTIMIZER_ARTEFACT_UNIT',
    'RESET_OPTIMIZER_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

    async [actionsTypes.FETCH_OPTIMIZER_GUIDE](context, payload) {
        context.commit(LOOKUP_OPTIMIZER_GUIDE);
        return await fetchGuide(payload)
            .then(data => {
                payload.res = data;
                context.commit(SET_OPTIMIZER_GUIDE, payload);
            })
            .catch(error => {
                payload.res = error;
                context.commit(SET_OPTIMIZER_GUIDE, payload);
            });
    },
    async [actionsTypes.FETCH_OPTIMIZER_TAGS]({ commit }, payload) {
        commit(LOOKUP_OPTIMIZER_TAGS);
        return await fetchTags(payload)
            .then(data => {
                payload.res = data;
                commit(SET_OPTIMIZER_TAGS, payload);
            })
            .catch(error => {
                payload.res = error;
                commit(SET_OPTIMIZER_TAGS, payload);
            });
    },
    async [actionsTypes.FETCH_OPTIMIZER_ARTEFACTS]({ commit }, payload) {
        commit(LOOKUP_OPTIMIZER_ARTEFACTS);
        return await fetchArtefacts(payload)
            .then(data => {
                payload.res = data;
                commit(SET_OPTIMIZER_ARTEFACTS, payload);
            })
            .catch(error => {
                payload.res = error;
                commit(SET_OPTIMIZER_ARTEFACTS, payload);
            });
    },
    async [actionsTypes.FETCH_OPTIMIZER_ARTEFACT](context, payload, prevArtifact) {
        // avoid extronuous network call if search exists
        context.commit(LOOKUP_OPTIMIZER_ARTEFACT);
        if (prevArtifact !== undefined) {
            return context.commit(SET_OPTIMIZER_ARTEFACT, prevArtifact);
        }

        if(['SPO'].includes(payload.type)) {
            const promise1 = fetchMock(payload);
            // const promise2 = fetchArtefact(payload);
        
            return await Promise.all([promise1])
              .then(values => {
                // console.log(values);
                let mock = {};
                try{
                  payload.mock = JSON.parse(JSON.stringify(values[0].data));
                } catch(e){
                  console.log(e);
                }
                let data = values[0];
                payload.res = data.data;
                context.commit(SET_OPTIMIZER_ARTEFACT, payload);
              })
              .catch(error => {
                payload.mock = {error: 'No Mock records found for the search criteria.'};
                payload.res = {error: 'No records found for the search criteria.'};
                context.commit(SET_OPTIMIZER_ARTEFACT, payload);
                // throw new Error(error);
              });
        } else {
            const promise1 = fetchTrend(payload);
            const promise2 = fetchRecommend(payload);
            const promise3 = fetchBundle(payload);

            return await Promise.all([promise1, promise2, promise3])
                .then(values => {
                    // console.log(values);
                    payload.res.trend = values[0];
                    payload.res.recommend = values[1];
                    payload.res.bundle = values[2];
                    context.commit(SET_OPTIMIZER_ARTEFACT, payload);
                })
                .catch(error => {
                    payload.res = { error: 'No records found for the search criteria.' };
                    context.commit(SET_OPTIMIZER_ARTEFACT, payload);
                    // throw new Error(error);
                });
        }

        // return await fetchArtefact(payload)
        //     .then(data => {
        //         payload.res = data;
        //         context.commit(SET_OPTIMIZER_ARTEFACT, payload);
        //     })
        //     .catch(error => {
        //         payload.res = { error: 'No records found for the search criteria.' };
        //         context.commit(SET_OPTIMIZER_ARTEFACT, payload);
        //         // throw new Error(error);
        //     });
    },
    async [actionsTypes.FETCH_OPTIMIZER_ARTEFACT_UNIT](context, payload, prevArtifact) {
        // avoid extronuous network call if search exists
        context.commit(LOOKUP_OPTIMIZER_ARTEFACT_UNIT);
        if (prevArtifact !== undefined) {
            return context.commit(SET_OPTIMIZER_ARTEFACT_UNIT, prevArtifact);
        }
        return await fetchArtefactUnit(payload)
            .then(data => {
                payload.res = data;
                context.commit(SET_OPTIMIZER_ARTEFACT_UNIT, payload);
            })
            .catch(error => {
                payload.res = { error: 'No records found for the given criteria.' };
                context.commit(SET_OPTIMIZER_ARTEFACT_UNIT, payload);
            });
    },
    [actionsTypes.RESET_OPTIMIZER_STATE]({ commit }) {
        commit(RESET_OPTIMIZER);
    }
};

/**
 * Users data actions
 */
const state = {
    ...initialState,
    guideOn: [],
    tagsCount: 0,
    artefactsCount: 0
};

const getters = {
    isLoading(state) {
        state.loading = false;
        for (let x in state.apiCallStatusTrack) {
            if (state.apiCallStatusTrack[x]) {
                state.loading = true;
                break;
            }
        }
        return state.loading;
    },
    tagsCount(state) {
        return state.tagsCount;
    },
    tags(state) {
        return state.tags;
    },
    artefactsCount(state) {
        return state.artefactsCount;
    },
    artefacts(state) {
        return state.artefacts;
    },
    artefact(state) {
        return state.artefact;
    },
    artefactUnit(state) {
        return state.artefactUnit;
    },
    guide(state) {
        return state.guide;
    },
    guideOn(state) {
        return state.guideOn;
    }
};

export default {
    mutations,
    actions,
    state,
    getters
};
